// A touch's AI-suggested links (MOTHER-288), over the routes of MOTHER-280 and MOTHER-281:
// GET /api/activities/:id/link-suggestions lists the ones still waiting for a person, and
// POST /api/activities/:id/link-suggestions/:suggestionId { decision } accepts or dismisses one.
// A touch (an email, text or call) is only ever suggested an opportunity or a project. The
// Activity tab shows them with Foundation's SuggestedLinksSection, as the meeting page does
// (meetings/linkSuggestions.ts, MOTHER-204).
import type { SuggestedLink } from '@olytic/foundation';

import { authenticatedFetch } from '../auth/authClient';
import { type LinkDecision, LinkSuggestionError } from '../meetings/linkSuggestions';
import { recordPath } from '../objects/routes';

export { type LinkDecision, LinkSuggestionError };

/** One suggestion as the GET route answers it. */
export interface ActivitySuggestion {
  suggestion_id: string;
  kind: 'opportunity' | 'project';
  record_id: string;
  record_name: string | null;
  confidence: number;
  evidence: string | null;
  created_at: string | null;
}

export interface ActivitySuggestions {
  activity_id: string;
  total_suggestions: number;
  suggestions: ActivitySuggestion[];
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

const suggestionsUrl = (activityId: string) => `/api/activities/${encodeURIComponent(activityId)}/link-suggestions`;

/** The body as JSON, or a LinkSuggestionError carrying the server's `error` text and the status. */
async function answer<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as (T & { error?: unknown }) | null;
  if (!res.ok || !body) {
    const error = typeof body?.error === 'string' && body.error.trim() !== '' ? body.error : `Something went wrong (${res.status}).`;
    throw new LinkSuggestionError(res.status, error);
  }
  return body;
}

/** The touch's suggestions still waiting for a person, surest first as the server sends them. */
export async function loadActivitySuggestions(activityId: string, fetcher: Fetcher = authenticatedFetch): Promise<ActivitySuggestions> {
  const body = await answer<ActivitySuggestions>(await fetcher(suggestionsUrl(activityId)));
  return { ...body, suggestions: Array.isArray(body.suggestions) ? body.suggestions : [] };
}

/** Accepts or dismisses one suggestion. Answers its new status; a refusal throws LinkSuggestionError (409 is "Already decided."). */
export async function decideActivitySuggestion(
  activityId: string,
  suggestionId: string,
  decision: LinkDecision,
  fetcher: Fetcher = authenticatedFetch,
): Promise<{ suggestion_id: string; status: 'accepted' | 'dismissed' }> {
  return answer(
    await fetcher(`${suggestionsUrl(activityId)}/${encodeURIComponent(suggestionId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ decision }),
    }),
  );
}

/** A suggestion in SuggestedLinksSection's shape, its name linking to the opportunity's or project's page. */
export function toSuggestedLink(s: ActivitySuggestion): SuggestedLink {
  return {
    id: s.suggestion_id,
    kind: s.kind,
    name: s.record_name ?? 'Unnamed',
    confidence: s.confidence,
    evidence: s.evidence,
    href: recordPath(s.kind === 'opportunity' ? 'opportunities' : 'projects', s.record_id),
  };
}
